import { RawTag } from '@/models/api/tags'
import {
  ColorFamilyName as BehaviorColorFamilyName,
  isColorFamilyName as isBehaviorColorFamilyName,
} from '@/models/app-config/behavior/config/color-family'
import { Either, left, right } from 'fp-ts/lib/Either'

export type ColorFamilyName = BehaviorColorFamilyName
export const isColorFamilyName = isBehaviorColorFamilyName

export function filterOnlyColorFamilyName(maybeNames: string[]): ColorFamilyName[] {
  return maybeNames.filter(n => isColorFamilyName(n)) as ColorFamilyName[]
}

export class ColorFamily {
  static createFromDto(raw: RawTag): Either<Error, ColorFamily> {
    const { id, name, pathname, title, display_level, description } = raw

    if (!isColorFamilyName(name)) {
      return left(new Error(`Unexpected color family name ${name}.`))
    }

    return right(
      new ColorFamily(id, name, pathname, title, display_level, description)
    )
  }

  constructor(
    readonly id: string,
    readonly name: ColorFamilyName,
    readonly pathname: string,
    readonly title: string,
    readonly displayLevel: number,
    readonly description: string
  ) {}
}

export class ColorFamilyList {
  static empty(): ColorFamilyList {
    return new ColorFamilyList([])
  }

  static valueOf(list: ColorFamily[]): ColorFamilyList {
    return new ColorFamilyList(list)
  }

  private constructor(
    public readonly list: ColorFamily[]
  ) {}

  get length(): number {
    return this.list.length
  }

  sortAscByDisplayLevel(): ColorFamilyList {
    return new ColorFamilyList(
      this.list.sort((a, b) => a.displayLevel - b.displayLevel)
    )
  }

  filter(f: (c: ColorFamily) => boolean): ColorFamilyList {
    return new ColorFamilyList(this.list.filter(f))
  }

  map<T>(f: (c: ColorFamily) => T): T[] {
    return this.list.map(f)
  }

  find(id: string): ColorFamily | undefined {
    return this.list.find(c => c.id === id)
  }
}
